import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { i18n } from 'i18n';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import { styled } from '@mui/material/styles';
import { MODEL_DRAW_WIDTH } from 'config';
import { CONTENT_WITH_SUB_NAV_OFFSET, CONTENT_WITH_SUB_NAV_MOBILE_OFFSET } from 'components/Layout/Layout';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const PREFIX = 'ModelDrawer';

const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
  resizeHandle: `${PREFIX}-resizeHandle`,
};

const MIN_DRAWER_WIDTH = 260;
const MAX_DRAWER_WIDTH = 720;

const Root = styled('div')((
  {
    theme,
  },
) => ({
  [`&.${classes.root}`]: {
    position: 'fixed',
    right: 0,
    top: `calc(${CONTENT_WITH_SUB_NAV_OFFSET}px + var(--stopes-toolbar-height, 0px))`,
    height: `calc(100% - ${CONTENT_WITH_SUB_NAV_OFFSET}px - var(--stopes-toolbar-height, 0px))`,
    zIndex: 1000,
    [theme.breakpoints.down('md')]: {
      top: `calc(${CONTENT_WITH_SUB_NAV_MOBILE_OFFSET}px + var(--stopes-toolbar-height, 0px))`,
      height: `calc(100% - ${CONTENT_WITH_SUB_NAV_MOBILE_OFFSET}px - var(--stopes-toolbar-height, 0px))`,
    },
  },

  [`& .${classes.paper}`]: {
    height: '100%',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    position: 'relative',
  },

  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.resizeHandle}`]: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 2,
    backgroundColor: 'transparent',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.4,
    },
  },
}));

interface ParamMap {
  Identifier: string,
  key: string,
}

interface ModelDrawerProps {
  backNavPath?: string;
  backNavText?: string;
  dynamicParams?: ParamMap[];
  isOpen: boolean;
  setIsOpen: (state: boolean) => void;
  onBack?: () => void;
  children?: React.ReactNode;
}

const ModelDrawer = ({ children, backNavPath, dynamicParams, backNavText, isOpen, onBack, setIsOpen }: ModelDrawerProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const [width, setWidth] = React.useState<number>(MODEL_DRAW_WIDTH);
  const [isResizing, setIsResizing] = React.useState(false);

  const processedBackPath = dynamicParams
    ? dynamicParams.reduce((finalPath, currentParam) => finalPath.replace(currentParam.Identifier, (params as any)[currentParam.key]), backNavPath ?? '')
    : backNavPath;

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Publish the live drawer width so overlays (HGL chart, tool banner) can stay clear of it.
  React.useEffect(() => {
    document.documentElement.style.setProperty('--stopes-drawer-width', isOpen ? `${width}px` : '0px');
  }, [isOpen, width]);

  React.useEffect(() => () => {
    document.documentElement.style.removeProperty('--stopes-drawer-width');
  }, []);

  React.useEffect(() => {
    if (!isResizing) return undefined;
    const handleMove = (e: MouseEvent) => {
      const next = window.innerWidth - e.clientX;
      const clamped = Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, next));
      setWidth(clamped);
    };
    const handleUp = () => setIsResizing(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing]);

  return (
    <Root className={classes.root}>
      <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={4}
          className={classes.paper}
          sx={{ width: `${width}px` }}
        >
          <Box
            className={classes.resizeHandle}
            onMouseDown={handleResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
          />
          <Box
            sx={{
              pt: 1,
              pb: 1,
            }}
          >
            <Box
              sx={{
                px: 2,
                display: 'flex',
                flexDirection: 'row-reverse',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                color="inherit"
                aria-label="close stope navigation"
                onClick={() => setIsOpen(false)}
                size="large"
              >
                <ChevronRightIcon />
              </IconButton>
              {
                (processedBackPath || onBack) && backNavText && (
                  <Button
                    aria-label="back navigation"
                    color="inherit"
                    onClick={
                      () => {
                        onBack && onBack();
                        processedBackPath && navigate(processedBackPath);
                      }
                    }
                    startIcon={<ArrowBackIcon />}
                  >
                    {/* Explicit space between the two parts — the old msgid ended with a
                        trailing space that translations dropped ("RETOUR ÀSTOPES"). */}
                    {`${i18n._('BACK TO')} ${i18n._(backNavText)}`}
                  </Button>
                )
              }
            </Box>
          </Box>
          <Box
            sx={{
              px: 2,
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {children}
          </Box>
        </Paper>
      </Slide>
    </Root>
  );
};

export default ModelDrawer;
